// Frontend Sentry error tracking.  Opt-in via REACT_APP_SENTRY_DSN env var
// baked at build time.  When unset, Sentry is not loaded and all helpers
// are no-ops.
//
// Captures:
//   - Uncaught JS errors
//   - Unhandled promise rejections
//   - React render errors (via SentryErrorBoundary at App root)
//
// Does NOT capture PII by default — the beforeSend hook strips potentially
// sensitive request/response bodies before shipping.

import * as Sentry from '@sentry/react'

let enabled = false

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN
  if (!dsn) return { enabled: false, reason: 'no-dsn' }
  try {
    Sentry.init({
      dsn,
      // Environment tag — filter dev/staging/prod in the Sentry UI.
      // REACT_APP_SENTRY_ENVIRONMENT is baked at build time so a staging
      // build can be tagged distinctly without changing NODE_ENV.
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'production',
      // Release tagging groups errors by deploy — flip on git commit sha
      // via REACT_APP_SENTRY_RELEASE at build time (CI can set this, dev
      // leaves it unset and Sentry auto-generates one).
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      // No perf tracing / session replay — just errors.  Cheap + focused.
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Filter noise before ship
      beforeSend(event, hint) {
        const err = hint?.originalException
        // ResizeObserver loop-limit is a benign browser bug on some sites
        if (typeof err?.message === 'string' && /ResizeObserver loop/.test(err.message)) return null
        // Ignore axios cancellations
        if (err?.name === 'CanceledError') return null
        // Ignore fetch/AbortController timeouts (HealthDot times out at 8s
        // by design; not a Sentry-worthy error)
        if (err?.name === 'AbortError') return null
        // Ignore auth 401s from expired tokens
        if (err?.response?.status === 401) return null
        // Ignore chunk load errors — usually the user is on a stale bundle
        // after a deploy.  Not actionable; just needs a reload.
        if (typeof err?.message === 'string' && /Loading chunk \d+ failed/.test(err.message)) return null
        return event
      },
    })
    enabled = true
    // eslint-disable-next-line no-console
    console.log('[sentry] initialized')
    return { enabled: true }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed:', err.message)
    return { enabled: false, reason: err.message }
  }
}

export function setUser(user) {
  if (!enabled || !user) return
  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      role: user.role,
    })
  } catch { /* ignore */ }
}

export function clearUser() {
  if (!enabled) return
  try { Sentry.setUser(null) } catch { /* ignore */ }
}

// Log a route-change breadcrumb so a Sentry error trace shows what page
// the user was on before the crash — critical debugging context for
// React-router SPAs where the URL doesn't appear in the raw stack.
export function logRouteBreadcrumb (pathname) {
  if (!enabled) return
  try {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: pathname,
      level: 'info',
    })
  } catch { /* ignore */ }
}

// Log an app-level event breadcrumb (login, logout, save-error, etc.)
// so Sentry stack traces have context about what the user was doing.
export function logAppBreadcrumb (message, data) {
  if (!enabled) return
  try {
    Sentry.addBreadcrumb({
      category: 'app',
      message,
      data,
      level: 'info',
    })
  } catch { /* ignore */ }
}

export const SentryErrorBoundary = Sentry.ErrorBoundary
